import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/api';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

const formatToISO = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function BookingEditPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [form, setForm] = useState({
    clientId: '',
    mediaResourceId: '',
    editionId: '',
    positionId: '',
    mediaType: '',
    platformDetails: '',
    timeFrom: '',
    timeTo: '',
    materialDueDate: '',
    notes: '',
    statusId: '',
    ownerId: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [clients, setClients] = useState([]);
  const [resources, setResources] = useState([]);
  const [editions, setEditions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [releaseDates, setReleaseDates] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingRes, clientsRes, resourcesRes, editionsRes, positionsRes, statusRes, usersRes] =
        await Promise.all([
          api.get(`/bookings/${bookingId}`),
          api.get('/master/clients'),
          api.get('/master/media-resources'),
          api.get('/master/editions'),
          api.get('/master/positions'),
          api.get('/statuses'),
          api.get('/users'),
        ]);

      const fetchedBooking = bookingRes.data.booking;
      setBooking(fetchedBooking);
      setClients(clientsRes.data.clients || []);
      setResources(resourcesRes.data.resources || []);
      setEditions(editionsRes.data.editions || []);
      setPositions(positionsRes.data.positions || []);
      setStatuses(statusRes.data.statuses || []);
      setUsers(usersRes.data.users || []);
      
      setReleaseDates(fetchedBooking.releaseDates?.map(rd => ({
        id: rd.id,
        date: formatToISO(rd.release_date),
        statusId: rd.status_id || ''
      })) || []);

      setForm({
        clientId: fetchedBooking.client_id || '',
        mediaResourceId: fetchedBooking.media_resource_id || '',
        editionId: fetchedBooking.edition_id || '',
        positionId: fetchedBooking.position_id || '',
        mediaType: fetchedBooking.media_type || '',
        platformDetails: fetchedBooking.platform_details || '',
        timeFrom: fetchedBooking.time_from || '',
        timeTo: fetchedBooking.time_to || '',
        materialDueDate: formatToISO(fetchedBooking.material_due_date),
        notes: fetchedBooking.notes || '',
        statusId: fetchedBooking.status_id || '',
        ownerId: fetchedBooking.owner_user_id || '',
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchData();
    }
  }, [bookingId]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Check if status is being set to 'Released' before any release date
    const targetStatus = statuses.find(s => s.id === parseInt(form.statusId, 10));
    if (targetStatus?.name === 'Released') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const hasFutureDate = releaseDates.some(rd => {
        if (!rd.date) return false;
        const rDate = new Date(rd.date);
        rDate.setHours(0, 0, 0, 0);
        return today < rDate;
      });

      if (hasFutureDate) {
        alert('You cannot change status to Released before the release date');
        return;
      }
    }

    setSaving(true);
    setMessage('');
    try {
      const payload = {
        clientId: form.clientId || null,
        mediaResourceId: form.mediaResourceId || null,
        editionId: form.editionId || null,
        positionId: form.positionId || null,
        mediaType: form.mediaType || null,
        platformDetails: form.platformDetails || null,
        timeFrom: form.timeFrom || null,
        timeTo: form.timeTo || null,
        materialDueDate: form.materialDueDate || null,
        notes: form.notes || null,
        statusId: form.statusId ? parseInt(form.statusId, 10) : null,
        ownerId: form.ownerId ? parseInt(form.ownerId, 10) : null,
        bookingSource: booking.booking_source,
        releaseDates: releaseDates
          .filter(rd => rd.date)
          .map(rd => ({
            releaseDate: rd.date,
            statusId: form.statusId ? parseInt(form.statusId, 10) : null, // Always use the main status
            isLocked: 0,
          })),
      };
      await api.patch(`/bookings/${bookingId}`, payload);
      setMessage('Booking updated successfully');
      await fetchData(); // Refresh data from server
    } catch (error) {
      console.error(error);
      setMessage('Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  const filteredResources = resources.filter(r => 
    r.media_type?.toUpperCase() === form.mediaType?.toUpperCase()
  );

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="card">
          <p>Booking not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="booking-details-card">
        <div className="details-header" style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>Edit Booking</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>Update booking information and scheduling</p>
          </div>
          <button className="btn-secondary" onClick={() => navigate('/bookings')}>
            Back to List
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {/* Core Information */}
            <div className="form-section" style={{ gridColumn: 'span 3', background: '#f8fafc', padding: '1.25rem', borderRadius: '1rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#334155' }}>Core Information</h3>
              <div className="details-form-grid" style={{ marginTop: 0 }}>
                <div className="form-field">
                  <label>Client</label>
                  <select value={form.clientId} onChange={handleChange('clientId')}>
                    <option value="">Select client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Media Resource</label>
                  <select value={form.mediaResourceId} onChange={handleChange('mediaResourceId')}>
                    <option value="">Select media resource</option>
                    {filteredResources.map((resource) => (
                      <option key={resource.id} value={resource.id}>{resource.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Media Type</label>
                  <select 
                    value={form.mediaType} 
                    onChange={(e) => {
                      setForm({ ...form, mediaType: e.target.value, mediaResourceId: '' });
                    }}
                  >
                    <option value="">Select Media Type</option>
                    <option value="Print">Print</option>
                    <option value="Radio">Radio</option>
                    <option value="TV">TV</option>
                    <option value="Digital">Digital</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Placement Details */}
            <div className="form-section" style={{ gridColumn: 'span 3', background: '#f8fafc', padding: '1.25rem', borderRadius: '1rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#334155' }}>Details</h3>
              <div className="details-form-grid" style={{ marginTop: 0 }}>
                <div className="form-field">
                  <label>Edition</label>
                  <select value={form.editionId} onChange={handleChange('editionId')}>
                    <option value="">No edition</option>
                    {editions.map((edition) => (
                      <option key={edition.id} value={edition.id}>{edition.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Position</label>
                  <select value={form.positionId} onChange={handleChange('positionId')}>
                    <option value="">No position</option>
                    {positions.map((position) => (
                      <option key={position.id} value={position.id}>{position.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Platform Details</label>
                  <input type="text" value={form.platformDetails} onChange={handleChange('platformDetails')} placeholder="e.g. Front Page, Sidebar" />
                </div>
              </div>
            </div>

            {/* Scheduling & Ownership */}
            <div className="form-section" style={{ gridColumn: 'span 3', background: '#f8fafc', padding: '1.25rem', borderRadius: '1rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#334155' }}>Scheduling & Ownership</h3>
              <div className="details-form-grid" style={{ marginTop: 0 }}>
                <div className="form-field">
                  <label>Material Due Date</label>
                  <DatePicker
                    selected={form.materialDueDate ? new Date(form.materialDueDate) : null}
                    onChange={(newDate) => {
                      const formattedDate = newDate ? 
                        `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}` : 
                        '';
                      setForm({ ...form, materialDueDate: formattedDate });
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yyyy"
                    className="datepicker-input"
                  />
                </div>
                <div className="form-field">
                  <label>Time From</label>
                  <input type="time" value={form.timeFrom} onChange={handleChange('timeFrom')} />
                </div>
                <div className="form-field">
                  <label>Time To</label>
                  <input type="time" value={form.timeTo} onChange={handleChange('timeTo')} />
                </div>
                {booking.booking_source !== 'PUBLICATION' && (
                  <div className="form-field">
                    <label>Booking Status</label>
                    <select value={form.statusId} onChange={handleChange('statusId')}>
                      <option value="">Select status</option>
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-field">
                  <label>Owner</label>
                  <select value={form.ownerId} onChange={handleChange('ownerId')}>
                    <option value="">Select owner</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name || u.email}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Release Dates Section */}
            <div className="form-section" style={{ gridColumn: 'span 3', background: '#f8fafc', padding: '1.25rem', borderRadius: '1rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#334155' }}>Release Dates</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {releaseDates.map((rd, idx) => (
                  <div key={rd.id || idx} style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '0.75rem',
                    alignItems: 'center',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div className="form-field" style={{ gap: '0.2rem' }}>
                      <label style={{ fontSize: '0.75rem', color: '#64748b' }}>Release Date</label>
                      <DatePicker
                        selected={rd.date ? new Date(rd.date) : null}
                        onChange={(newDate) => {
                          const formattedDate = newDate ? 
                            `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}` : 
                            '';
                          const dates = [...releaseDates];
                          dates[idx] = { ...dates[idx], date: formattedDate };
                          setReleaseDates(dates);
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                        className="datepicker-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setReleaseDates(releaseDates.filter((_, i) => i !== idx))}
                      style={{
                        padding: '0.65rem 1rem',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        marginTop: '1.2rem'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setReleaseDates([...releaseDates, { date: '', statusId: '' }])}
                  style={{
                    padding: '0.75rem',
                    background: '#f0f4ff',
                    color: '#1c4fe0',
                    border: '1px solid #1c4fe0',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    width: 'fit-content'
                  }}
                >
                  + Add Release Date
                </button>
              </div>
            </div>

            <div className="form-field" style={{ gridColumn: 'span 3' }}>
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Notes</label>
              <textarea 
                value={form.notes} 
                onChange={handleChange('notes')} 
                rows={4} 
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}
                placeholder="Add any additional instructions or notes here..."
              />
            </div>
          </div>

          <div style={{ marginTop: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
            <button className="btn-primary" type="submit" disabled={saving} style={{ padding: '0.75rem 2.5rem', fontSize: '1rem' }}>
              {saving ? 'Saving Changes...' : 'Save Changes'}
            </button>
            {message && (
              <span style={{ 
                color: message.includes('success') ? '#10b981' : '#ef4444',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {message.includes('success') ? '✓' : '⚠'} {message}
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
